// Slice 38 (ADR 0034) — Supabase magic-link callback landing page.
//
// Supabase's magic link redirects here with the session either
// already resolved (OAuth implicit flow) or hinted in the URL hash.
// supabase-js handles both inside `getSession()`; we just wait on it
// and hand the resulting access_token to AuthContext.
//
// Only rendered under VITE_AUTH_MODE=supabase. In dev mode this
// route is still registered but will never be hit (the dev-login
// screen navigates straight to /home on success).

import { useEffect, useState, type JSX } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext.js';
import { getAuthMode, getSupabaseClient } from '../auth/supabase-client.js';

type Status = 'resolving' | 'done' | 'failed';

export function AuthCallbackPage(): JSX.Element {
  const { token, completeSupabaseSignIn } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState<Status>('resolving');
  const [error, setError] = useState<string | null>(null);

  const mode = getAuthMode();

  useEffect(() => {
    if (mode !== 'supabase') return;
    let cancelled = false;
    (async () => {
      try {
        const client = getSupabaseClient();
        const { data, error: supaErr } = await client.auth.getSession();
        if (supaErr) throw supaErr;
        const session = data.session;
        if (!session || !session.access_token) {
          throw new Error('Sign-in link did not produce a session. Try requesting a new link.');
        }
        if (cancelled) return;
        completeSupabaseSignIn(session.access_token, session.user?.email ?? null);
        setStatus('done');
        navigate('/home', { replace: true });
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Sign-in failed.');
        setStatus('failed');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mode, completeSupabaseSignIn, navigate]);

  if (mode !== 'supabase') return <Navigate to="/" replace />;
  if (token && status !== 'resolving') return <Navigate to="/home" replace />;

  return (
    <main className="auth-wrap">
      <section className="card auth-card">
        <h1>Tabletop</h1>
        {status === 'resolving' ? (
          <p className="muted">Finishing sign-in…</p>
        ) : status === 'failed' ? (
          <>
            <p role="alert" className="error">
              {error ?? 'Sign-in failed.'}
            </p>
            <a href="/">Try again</a>
          </>
        ) : (
          <p className="muted">Signed in. Redirecting…</p>
        )}
      </section>
    </main>
  );
}
